/**
 * Operations log + Undo API client (Undo/History P2).
 *
 * batch-apply groups one batch gesture's writes under a client-generated
 * operationId with server-side prior-state capture; undo restores them.
 * Chunks of one operation MUST be posted sequentially (server seq contract).
 */

import { apiFetch } from './apiFetch';

export type OperationKind = 'batch_rotate' | 'batch_status' | 'batch_delete' | 'batch_fit' | 'bulk_rederive' | 'bulk_create' | 'drive_import';

export interface BatchApplyItem {
  id: string;
  version: number;
  geometry_json?: string;
  properties_json?: string;
  status?: 'open' | 'in_progress' | 'resolved';
  note?: string;
}

export interface BatchApplyResult {
  operationId: string;
  applied: number;
  results: Array<{ id: string; version: number }>;
  staleConflicts: Array<{ id: string; currentVersion: number; yourVersion: number }>;
  missing: string[];
  noop: string[];
}

export async function batchApplyIndications(
  siteId: string,
  operationId: string,
  kind: OperationKind,
  label: string,
  items: BatchApplyItem[],
): Promise<BatchApplyResult> {
  return apiFetch<BatchApplyResult>(`/api/v2/sites/${siteId}/indications/batch-apply`, {
    method: 'POST',
    body: JSON.stringify({ operationId, kind, label, items }),
  });
}

export interface UndoResult {
  operationId: string;
  undoOperationId: string | null;
  restored: number;
  results: Array<{ id: string; version: number }>;
  conflicts: Array<{ id: string; reason: string; currentVersion?: number }>;
  operationStatus: string;
}

export async function undoOperation(siteId: string, operationId: string): Promise<UndoResult> {
  return apiFetch<UndoResult>(`/api/v2/sites/${siteId}/operations/${operationId}/undo`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export interface OperationSummary {
  id: string;
  site_id: string;
  user_id: string | null;
  kind: string;
  label: string | null;
  change_count: number;
  status: string;
  undo_of: string | null;
  created_at: string;
  undone_at: string | null;
}

export async function listOperations(siteId: string, limit = 50): Promise<OperationSummary[]> {
  const res = await apiFetch<{ operations: OperationSummary[] }>(
    `/api/v2/sites/${siteId}/operations?limit=${limit}`,
  );
  return res.operations;
}
